import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Sparkles, LayoutGrid, Store, Wallet, Shield, Menu, Upload, Coins, User, Building2 } from "lucide-react";
import { toast } from "sonner";
import { RuneLogo } from "@/components/RuneLogo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

function VerifyEmailBanner({ user }) {
  // Only password accounts land here unverified; Google accounts are pre-verified.
  if (!user || user.email_verified || !user.has_password) return null;
  const resend = async () => {
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent — check your inbox.");
    } catch (e) {
      toast.error("Could not send right now. Try again shortly.");
    }
  };
  return (
    <div data-testid="verify-email-banner" className="border-b border-[color:rgba(212,175,55,0.3)] bg-[color:var(--rb-gold-soft)] px-4 py-2 text-center text-xs text-[color:var(--rb-gold)]">
      Verify your email to publish to the marketplace.{" "}
      <button onClick={resend} className="underline hover:no-underline" data-testid="resend-verify-button">Resend link</button>
    </div>
  );
}
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

// Wallet/earnings are mothballed, not removed (registry pivot design.md "Mothballed, not
// removed"): the routes/pages keep working unchanged, but they're no longer top-level nav —
// tucked into the avatar menu below so the primary bar reads as a registry product.
const NAV = [
  { to: "/build", label: "Build", icon: Sparkles, testid: "nav-build-link" },
  { to: "/upload", label: "Upload", icon: Upload, testid: "nav-upload-link" },
  { to: "/marketplace", label: "Marketplace", icon: Store, testid: "nav-marketplace-link" },
  { to: "/my-skills", label: "My Artifacts", icon: LayoutGrid, testid: "nav-my-skills-link" },
];

export const AppShell = ({ children, fullWidth = false }) => {
  const { user, org, logout, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <div className="min-h-screen">
      <VerifyEmailBanner user={user} />
      <header
        data-testid="app-top-nav"
        className="sticky top-0 z-40 border-b border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)]/80 backdrop-blur"
      >
        <div className="mx-auto flex h-14 max-w-[1280px] items-center gap-2 px-4 sm:px-6 lg:px-8 md:h-16">
          <Link to="/build" className="flex items-center gap-2.5">
            <RuneLogo className="h-7 w-7" />
            <span className="rb-display text-lg font-semibold tracking-tight text-[color:var(--rb-text)]">Runebox</span>
          </Link>

          <nav className="ml-6 hidden items-center gap-1 md:flex">
            {NAV.map(({ to, label, icon: Icon, testid }) => (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive(to)
                    ? "bg-[color:rgba(45,212,191,0.10)] text-[color:var(--rb-accent)]"
                    : "text-[color:var(--rb-text-dim)] hover:bg-white/5 hover:text-[color:var(--rb-text)]"
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </Link>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            {!user && (
              <button
                onClick={login}
                data-testid="nav-signin"
                className="rounded-lg bg-[color:var(--rb-accent)] px-4 py-2 text-sm font-semibold text-[color:var(--rb-void)] transition-opacity hover:opacity-90"
              >
                Sign in
              </button>
            )}
            {/* One org per account (2026-07-13): in an org -> a plain link to it; not in one ->
                the team CTA. The old multi-org switcher dropdown is gone. */}
            {user && !org && (
              <Link
                to="/orgs/new"
                data-testid="nav-team-cta"
                className="hidden items-center gap-1.5 rounded-full border border-[color:rgba(212,175,55,0.4)] bg-[color:var(--rb-gold-soft)] px-3 py-1.5 text-sm font-medium text-[color:var(--rb-gold)] transition-colors hover:bg-[color:rgba(212,175,55,0.18)] sm:flex"
              >
                <Building2 className="h-4 w-4" /> Use with your team
              </Link>
            )}
            {user && org && (
              <Link
                to={`/orgs/${org.slug}`}
                data-testid="nav-org-link"
                className="flex items-center gap-1.5 rounded-full border border-[color:var(--rb-border)] px-3 py-1.5 text-sm text-[color:var(--rb-text-dim)] transition-colors hover:bg-white/5"
              >
                <Building2 className="h-4 w-4" />
                <span className="max-w-[120px] truncate">{org.name}</span>
              </Link>
            )}
            {user && <DropdownMenu>
              <DropdownMenuTrigger className="md:hidden rounded-lg p-2 text-[color:var(--rb-text-dim)] hover:bg-white/5" data-testid="mobile-nav-trigger">
                <Menu className="h-5 w-5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)]">
                {NAV.map(({ to, label, icon: Icon }) => (
                  <DropdownMenuItem key={to} onClick={() => navigate(to)} className="gap-2">
                    <Icon className="h-4 w-4" /> {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>}

            {user && <DropdownMenu>
              <DropdownMenuTrigger data-testid="nav-avatar-menu" className="flex items-center gap-2 rounded-full outline-none">
                <Avatar className="h-8 w-8 border border-[color:var(--rb-border)]">
                  <AvatarImage src={user?.avatar} alt={user?.name} />
                  <AvatarFallback className="bg-[color:var(--rb-surface)] text-xs text-[color:var(--rb-accent)]">
                    {(user?.name || "U").slice(0, 1).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)]">
                <DropdownMenuLabel className="truncate">
                  <div className="text-sm text-[color:var(--rb-accent)]">@{user?.username || "…"}</div>
                  <div className="truncate text-xs text-[color:var(--rb-text-faint)]">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} className="gap-2 text-[color:var(--rb-text)]" data-testid="nav-profile-link">
                  <User className="h-4 w-4" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/wallet")} className="gap-2 text-[color:var(--rb-text)]" data-testid="nav-wallet-link">
                  <Coins className="h-4 w-4" /> Wallet
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/earnings")} className="gap-2 text-[color:var(--rb-text)]" data-testid="nav-earnings-link">
                  <Wallet className="h-4 w-4" /> Earnings
                </DropdownMenuItem>
                {user?.is_owner && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} className="gap-2" data-testid="nav-admin-link">
                    <Shield className="h-4 w-4" /> Owner console
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} className="gap-2 text-[color:var(--rb-text)]" data-testid="logout-button">
                  <LogOut className="h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>}
          </div>
        </div>
      </header>

      <main className={fullWidth ? "" : "mx-auto max-w-[1280px] px-4 py-6 sm:px-6 lg:px-8 md:py-10"}>
        {children}
      </main>

      <footer className="border-t border-[color:var(--rb-border)] py-6">
        <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-between gap-2 px-4 text-xs text-[color:var(--rb-text-faint)] sm:flex-row sm:px-6 lg:px-8">
          <span>&copy; {new Date().getFullYear()} Runebox</span>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-[color:var(--rb-text-dim)]">Terms</Link>
            <Link to="/privacy" className="hover:text-[color:var(--rb-text-dim)]">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
